import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { CommissionPayee } from '@aeris/contracts'
import { Button, Combobox, Field } from '@aeris/ui'

import {
  registerCommissionPayeeParty,
  setCommissionPayee,
} from '../lib/api/commissions.ts'
import { fetchParties } from '../lib/api/parties.ts'
import { ApiError } from '../lib/api.ts'
import { useDebounced } from '../lib/use-debounced.ts'

/**
 * Quem recebe a comissão de uma pessoa da equipe (F137, F235).
 *
 * O título a pagar exige uma Pessoa do cadastro. Escolher uma é a busca de
 * sempre; quando ela não existe, "Cadastrar" cria a Pessoa com o nome da
 * conta e já a liga, num passo só.
 *
 * Saiu do diálogo da Equipe para servir também ao Fechar mês: o gerente da
 * Elite clicava em fechar, lia "falta quem receba" e não sabia que o lugar
 * era Equipe → ⋯ → Comissão — e a Rayane nem estava no cadastro.
 */
export const CommissionPayeePicker = ({
  userId,
  displayName,
  payee,
  label = 'Quem recebe',
  onSaved,
  onError,
}: {
  userId: string
  displayName: string
  payee: CommissionPayee | null
  label?: string
  onSaved: (message: string) => void
  onError: (message: string) => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const term = useDebounced(search.trim())

  const parties = useQuery({
    queryKey: ['parties', 'commission-payee', term],
    queryFn: ({ signal }) =>
      fetchParties({ search: term, status: 'active', pageSize: 8 }, signal),
    enabled: term.length > 1,
  })

  const saved = async (chosen: CommissionPayee): Promise<void> => {
    setSearch('')
    onSaved(`${chosen.name} recebe a comissão de ${displayName}.`)
    await queryClient.invalidateQueries({ queryKey: ['commissions'] })
  }

  const failed = (cause: unknown, fallback: string): void => {
    onError(cause instanceof ApiError ? cause.message : fallback)
  }

  const choose = useMutation({
    mutationFn: async (partyId: string) =>
      await setCommissionPayee(userId, {
        partyId,
        version: payee?.version ?? 0,
      }),
    onSuccess: saved,
    onError: (cause: unknown) => {
      failed(cause, 'Não foi possível gravar quem recebe.')
    },
  })

  const register = useMutation({
    mutationFn: async () =>
      await registerCommissionPayeeParty(userId, {
        version: payee?.version ?? 0,
      }),
    onSuccess: saved,
    onError: (cause: unknown) => {
      failed(cause, `Não foi possível cadastrar ${displayName}.`)
    },
  })

  return (
    <div className="flex flex-col gap-2">
      <Field
        label={label}
        hint={
          payee === null
            ? 'A pessoa do cadastro que vai no título a pagar. Não está no cadastro? Use o botão abaixo.'
            : 'A pessoa do cadastro que vai no título a pagar.'
        }
      >
        {(field) => (
          <Combobox
            id={field.id}
            aria-describedby={field['aria-describedby']}
            value={payee?.partyId ?? null}
            selectedLabel={payee?.name ?? null}
            onValueChange={(partyId) => {
              if (partyId === null) return
              choose.mutate(partyId)
            }}
            items={(parties.data?.items ?? []).map((party) => ({
              value: party.id,
              label: party.legalName,
            }))}
            search={search}
            onSearchChange={setSearch}
            loading={parties.isFetching || choose.isPending}
            placeholder="Buscar pessoa pelo nome"
            emptyMessage={
              term.length < 2
                ? 'Digite ao menos 2 letras.'
                : `Ninguém com “${term}” no cadastro.`
            }
          />
        )}
      </Field>
      {payee === null ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          loading={register.isPending}
          onClick={() => {
            register.mutate()
          }}
        >
          {`Cadastrar “${displayName}” como pessoa nova`}
        </Button>
      ) : null}
    </div>
  )
}
